import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  VStack,
  HStack,
  Text,
  Input,
  useDisclosure,
  ScaleFade,
  Badge,
  useColorModeValue,
  useToast,
  Tooltip,
} from '@chakra-ui/react';
import {
  ChatIcon,
  CloseIcon,
  ArrowForwardIcon,
  SearchIcon,
} from '@chakra-ui/icons';
import { chatService, Message } from '../../services/chatService';
import { chatStorageService } from '../../services/chatStorageService';
import { sessionService, Session } from '../../services/sessionService';
import ChatMessage from '../../components/AIChat/ChatMessage';
import QuickReplies from './QuickReplies';
import TypingIndicator from './TypingIndicator';
import SessionManager from './SessionManager';
import ChatSearch from './ChatSearch';
import ExportMenu from './ExportMenu';
import './ChatWidget.css';

const ChatWidget: React.FC = () => {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const toast = useToast();

  // Track if chat was restored from storage
  const [wasRestored] = useState(() => {
    const stored = chatStorageService.loadChat();
    return stored && stored.length > 1;
  });

  // Initialize messages - try to load from localStorage first
  const [messages, setMessages] = useState<Message[]>(() => {
    const stored = chatStorageService.loadChat();
    if (stored && stored.length > 0) {
      return stored;
    }
    return [
      {
        role: 'assistant',
        content:
          "Hi! 👋 I'm here to help you learn about our **gaming solutions**.\n\nI can help you with:\n- Information about our **Club App**\n- Game features and options\n- Pricing and demo requests\n- Technical support\n\nHow can I assist you today?",
        timestamp: new Date(),
      },
    ];
  });
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(() => {
    // Only show quick replies if starting fresh
    const stored = chatStorageService.loadChat();
    return !stored || stored.length <= 1;
  });
  const [showTypingIndicator, setShowTypingIndicator] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [sessionName, setSessionName] = useState('Chat');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasShownRestoreNotification = useRef(false);
  const messageRefs = useRef<(HTMLDivElement | null)[]>([]);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const messagesBg = useColorModeValue('gray.50', 'gray.900');
  const headerBg = useColorModeValue('primary.500', 'primary.600');
  const userMessageBg = useColorModeValue('primary.500', 'primary.600');
  const assistantMessageBg = useColorModeValue('white', 'gray.700');
  const assistantMessageColor = useColorModeValue('gray.800', 'white');

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
    if (isOpen) {
      setHasUnread(false);
      inputRef.current?.focus();

      // Show notification if chat was restored
      if (wasRestored && !hasShownRestoreNotification.current) {
        hasShownRestoreNotification.current = true;
        const storageInfo = chatStorageService.getStorageInfo();
        const hoursLeft = storageInfo.expiresIn
          ? Math.floor(storageInfo.expiresIn / (1000 * 60 * 60))
          : 0;

        toast({
          title: 'Chat history restored',
          description: `${storageInfo.messageCount} messages loaded. Expires in ${hoursLeft}h.`,
          status: 'info',
          duration: 3000,
          isClosable: true,
          position: 'top',
        });
      }
    }
  }, [isOpen, wasRestored, toast]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Save messages to localStorage and session whenever they change
  useEffect(() => {
    if (messages.length > 1) {
      chatStorageService.saveChat(messages);
      if (currentSessionId) {
        sessionService.updateSession(currentSessionId, messages);
      }
    }
  }, [messages, currentSessionId]);

  const handleSendMessage = async (message?: string) => {
    const messageToSend = message || inputMessage.trim();
    if (!messageToSend || isLoading) return;

    // Check for sensitive information before sending
    if (!chatStorageService.shouldPersistMessage(messageToSend)) {
      toast({
        title: 'Privacy Notice',
        description:
          'This message contains sensitive information and will not be saved.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
        position: 'top',
      });
    }

    const userMessage: Message = {
      role: 'user',
      content: messageToSend,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    // Hide quick replies after first user message
    if (messages.length <= 1) {
      setShowQuickReplies(false);
    }

    // Add realistic delay before showing typing indicator (simulates natural pause)
    const typingTimeout = setTimeout(() => {
      setShowTypingIndicator(true);
    }, 300);

    try {
      const response = await chatService.sendMessage(
        userMessage.content,
        messages
      );

      const assistantMessage: Message = {
        role: 'assistant',
        content: response.message,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Show unread badge if chat is closed
      if (!isOpen) {
        setHasUnread(true);
      }
    } catch (error) {
      const errorMessage: Message = {
        role: 'assistant',
        content:
          error instanceof Error
            ? error.message
            : 'Sorry, something went wrong. Please try again.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      clearTimeout(typingTimeout);
      setIsLoading(false);
      setShowTypingIndicator(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClearChat = () => {
    setMessages([
      {
        role: 'assistant',
        content:
          "Chat cleared! 👋 I'm ready to help you again.\n\nWhat would you like to know about our gaming solutions?",
        timestamp: new Date(),
      },
    ]);
    chatService.resetConversation();
    chatStorageService.clearChat();
    setShowQuickReplies(true);

    toast({
      title: 'Chat cleared',
      description: 'Your conversation history has been deleted.',
      status: 'info',
      duration: 2000,
      isClosable: true,
      position: 'top',
    });
  };

  const handleQuickReply = (message: string) => {
    handleSendMessage(message);
  };

  const handleRegenerate = async (messageIndex: number) => {
    // Find the user message that prompted this AI response
    if (messageIndex > 0 && messages[messageIndex].role === 'assistant') {
      const userMessage = messages[messageIndex - 1];

      if (userMessage.role === 'user') {
        // Remove the AI response that's being regenerated
        setMessages(prev => prev.filter((_, index) => index !== messageIndex));
        setIsLoading(true);
        setShowTypingIndicator(false);

        // Add realistic delay before showing typing indicator
        const typingTimeout = setTimeout(() => {
          setShowTypingIndicator(true);
        }, 300);

        try {
          // Get conversation history up to the user message
          const historyUpToMessage = messages.slice(0, messageIndex - 1);

          const response = await chatService.sendMessage(
            userMessage.content,
            historyUpToMessage
          );

          const assistantMessage: Message = {
            role: 'assistant',
            content: response.message,
            timestamp: new Date(),
          };

          // Insert the new response at the same position
          setMessages(prev => {
            const newMessages = [...prev];
            newMessages.splice(messageIndex, 0, assistantMessage);
            return newMessages;
          });
        } catch (error) {
          const errorMessage: Message = {
            role: 'assistant',
            content:
              error instanceof Error
                ? error.message
                : 'Sorry, something went wrong. Please try again.',
            timestamp: new Date(),
          };
          setMessages(prev => {
            const newMessages = [...prev];
            newMessages.splice(messageIndex, 0, errorMessage);
            return newMessages;
          });
        } finally {
          clearTimeout(typingTimeout);
          setIsLoading(false);
          setShowTypingIndicator(false);
        }
      }
    }
  };

  const handleFeedback = (messageIndex: number, feedback: 'up' | 'down') => {
    // Store feedback - this could be sent to analytics or backend
    console.log(`Feedback for message ${messageIndex}:`, feedback);

    // You can implement analytics tracking here
    // Example: analytics.track('chat_feedback', { messageIndex, feedback });
  };

  const handleSessionChange = (session: Session) => {
    setCurrentSessionId(session.id);
    setSessionName(session.name);
    setMessages(
      session.messages.length > 0
        ? session.messages
        : [
            {
              role: 'assistant',
              content:
                "Hi! 👋 I'm here to help you learn about our **gaming solutions**.\n\nI can help you with:\n- Information about our **Club App**\n- Game features and options\n- Pricing and demo requests\n- Technical support\n\nHow can I assist you today?",
              timestamp: new Date(),
            },
          ]
    );
    setShowQuickReplies(session.messages.length <= 1);
    chatService.resetConversation();

    toast({
      title: 'Session loaded',
      description: `Switched to "${session.name}"`,
      status: 'info',
      duration: 2000,
      isClosable: true,
      position: 'top',
    });
  };

  const handleNewSession = () => {
    const session = sessionService.createSession();
    setCurrentSessionId(session.id);
    setSessionName(session.name);
    setMessages([
      {
        role: 'assistant',
        content:
          "Hi! 👋 I'm here to help you learn about our **gaming solutions**.\n\nI can help you with:\n- Information about our **Club App**\n- Game features and options\n- Pricing and demo requests\n- Technical support\n\nHow can I assist you today?",
        timestamp: new Date(),
      },
    ]);
    setShowQuickReplies(true);
    chatService.resetConversation();

    toast({
      title: 'New session created',
      description: `Started "${session.name}"`,
      status: 'success',
      duration: 2000,
      isClosable: true,
      position: 'top',
    });
  };

  const handleSearchResultClick = (index: number) => {
    messageRefs.current[index]?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  };

  return (
    <>
      {/* Floating Chat Button */}
      <Box
        position='fixed'
        bottom='24px'
        right='24px'
        zIndex={1000}
        className='chat-widget-button'
      >
        <ScaleFade in={!isOpen} unmountOnExit>
          <Box position='relative'>
            <IconButton
              aria-label='Open chat'
              icon={<ChatIcon boxSize={6} />}
              onClick={onToggle}
              size='lg'
              colorScheme='primary'
              borderRadius='full'
              boxShadow='xl'
              width='60px'
              height='60px'
              _hover={{ transform: 'scale(1.1)' }}
              transition='all 0.2s'
            />
            {hasUnread && (
              <Badge
                position='absolute'
                top='-2px'
                right='-2px'
                colorScheme='red'
                borderRadius='full'
                boxSize='12px'
              />
            )}
          </Box>
        </ScaleFade>
      </Box>

      {/* Chat Window */}
      {isOpen && (
        <Box
          className='chat-window-container'
          bg={bgColor}
          borderRadius='lg'
          boxShadow='2xl'
          border='1px'
          borderColor={borderColor}
          display='flex'
          flexDirection='column'
          overflow='hidden'
          zIndex={999}
        >
          {/* Header */}
          <HStack
            bg={headerBg}
            color='white'
            p={4}
            justifyContent='space-between'
          >
            <HStack>
              <ChatIcon />
              <VStack align='start' spacing={0}>
                <Text fontWeight='bold'>Playzura AI</Text>
                {currentSessionId && (
                  <Text fontSize='xs' opacity={0.8}>
                    {sessionName}
                  </Text>
                )}
              </VStack>
            </HStack>
            <HStack spacing={1}>
              <Tooltip label='Search messages' placement='left'>
                <IconButton
                  aria-label='Search'
                  icon={<SearchIcon />}
                  size='sm'
                  variant='ghost'
                  color='white'
                  onClick={() => setIsSearchOpen(!isSearchOpen)}
                  _hover={{ bg: 'whiteAlpha.200' }}
                  isDisabled={messages.length <= 1}
                />
              </Tooltip>
              <ExportMenu messages={messages} sessionName={sessionName} />
              <SessionManager
                currentSessionId={currentSessionId}
                onSessionChange={handleSessionChange}
                onNewSession={handleNewSession}
              />
              <Button
                size='sm'
                variant='ghost'
                color='white'
                onClick={handleClearChat}
                _hover={{ bg: 'whiteAlpha.200' }}
              >
                Clear
              </Button>
              <IconButton
                aria-label='Close chat'
                icon={<CloseIcon />}
                size='sm'
                variant='ghost'
                color='white'
                onClick={onClose}
                _hover={{ bg: 'whiteAlpha.200' }}
              />
            </HStack>
          </HStack>

          {/* Search Bar */}
          <ChatSearch
            messages={messages}
            onResultClick={handleSearchResultClick}
            isOpen={isSearchOpen}
            onClose={() => setIsSearchOpen(false)}
            onQueryChange={setSearchQuery}
          />

          {/* Messages */}
          <VStack
            flex={1}
            overflowY='auto'
            p={4}
            spacing={3}
            align='stretch'
            bg={messagesBg}
          >
            {messages.map((message, index) => (
              <Box
                key={index}
                ref={el => (messageRefs.current[index] = el)}
                display='flex'
                flexDirection='column'
                alignSelf={message.role === 'user' ? 'flex-end' : 'flex-start'}
                width='100%'
              >
                <ChatMessage
                  message={message}
                  messageIndex={index}
                  userBg={userMessageBg}
                  assistantBg={assistantMessageBg}
                  assistantColor={assistantMessageColor}
                  onRegenerate={handleRegenerate}
                  onFeedback={handleFeedback}
                  searchQuery={searchQuery}
                />
              </Box>
            ))}
            {isLoading && showTypingIndicator && (
              <TypingIndicator assistantBg={assistantMessageBg} />
            )}
            <div ref={messagesEndRef} />
          </VStack>

          {/* Quick Replies */}
          {showQuickReplies && messages.length === 1 && (
            <Box
              p={3}
              bg={bgColor}
              borderTop='1px'
              borderBottom='1px'
              borderColor={borderColor}
            >
              <QuickReplies
                onSelectReply={handleQuickReply}
                isLoading={isLoading}
              />
            </Box>
          )}

          {/* Input */}
          <HStack p={4} bg={bgColor} borderTop='1px' borderColor={borderColor}>
            <Input
              ref={inputRef}
              placeholder='Type your message...'
              value={inputMessage}
              onChange={e => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              borderRadius='full'
              color={assistantMessageColor}
              _focus={{
                borderColor: 'primary.500',
                boxShadow: '0 0 0 1px var(--chakra-colors-primary-500)',
              }}
            />
            <IconButton
              aria-label='Send message'
              icon={<ArrowForwardIcon />}
              onClick={() => handleSendMessage()}
              isLoading={isLoading}
              colorScheme='primary'
              borderRadius='full'
            />
          </HStack>
        </Box>
      )}
    </>
  );
};

export default ChatWidget;
